import { useState, useRef, useEffect } from "react";
import { Compass, X, Send, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const SESSION_KEY = "voluntold_scout_session";
export const SUGGESTIONS = [
  "My star player keeps arguing calls — help.",
  "How do I run a good first practice?",
  "A parent is upset about playing time.",
  "My kid gets nervous before games.",
];

// Only linkify known in-app routes so phrases like "coach/parent" or "3/4" stay plain text
const ROUTE_RE = /(\/(?:assessment|shop|products|about|tools\/(?:practice-plan|parent-script)|category\/(?:coaches-corner|parent-sideline|practice-plans)|legal\/(?:terms|privacy|refund|cookies)|article\/[a-z0-9-]+))\b/gi;
const IS_ROUTE = /^\/(?:assessment|shop|products|about|tools\/(?:practice-plan|parent-script)|category\/(?:coaches-corner|parent-sideline|practice-plans)|legal\/(?:terms|privacy|refund|cookies)|article\/[a-z0-9-]+)$/i;

function renderText(text) {
  const parts = text.split(ROUTE_RE);
  return parts.map((p, i) =>
    IS_ROUTE.test(p) ? (
      <Link key={i} to={p} className="text-[#E8A33D] underline hover:text-[#D6922C]">{p}</Link>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export default function ScoutChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hey — I'm Scout. Ask me anything about coaching or being a sports parent, and I'll point you to the right play. What's going on?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(localStorage.getItem(SESSION_KEY) || null);
  const [handoff, setHandoff] = useState(false);
  const [hf, setHf] = useState({ name: "", email: "", question: "" });
  const [hstatus, setHstatus] = useState(null);
  const [visible, setVisible] = useState(false);
  const [pending, setPending] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  // Reveal the launcher only after the user scrolls past the hero, so it never covers above-the-fold content.
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 240);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // "Ask Scout about this" from a Quick Play result seeds a dedicated session.
  useEffect(() => {
    const onOpen = (e) => {
      const { sessionId, intro, message } = e.detail || {};
      if (sessionId) {
        setSession(sessionId);
        localStorage.setItem(SESSION_KEY, sessionId);
        setMsgs([{ role: "assistant", content: intro || "Ask me anything about your Quick Play." }]);
      }
      if (message) setPending(message);
      setVisible(true);
      setOpen(true);
    };
    window.addEventListener("open-scout", onOpen);
    return () => window.removeEventListener("open-scout", onOpen);
  }, []);

  const sendHandoff = async (e) => {
    e.preventDefault();
    if (!hf.email || !hf.question) return;
    setHstatus("sending");
    try {
      await api.post("/scout/handoff", { ...hf, session_id: session });
      setHstatus("ok");
      setHf({ name: "", email: "", question: "" });
    } catch {
      setHstatus("err");
    }
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/scout/chat", { session_id: session, message });
      if (data.session_id && data.session_id !== session) {
        setSession(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      const msg = e.response?.status === 429 ? e.response.data.detail
        : e.response?.status === 422 ? "That message is a bit long. Keep it under 1,000 characters."
        : "I glitched for a sec — try that again in a moment.";
      setMsgs((m) => [...m, { role: "assistant", content: msg }]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (pending && !busy) { setPending(null); send(pending); }
  }, [pending, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="scout-launcher"
        aria-label={open ? "Close Scout" : "Ask Scout"}
        style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom))", right: "1.25rem" }}
        className={`fixed z-[60] flex items-center gap-2 bg-[#2D2D2D] text-[#F3F1EC] pl-3.5 pr-4 py-3 border-2 border-[#1A1A1A] font-head uppercase tracking-wide text-sm shadow-lg transition-all duration-300 ease-out hover:bg-[#242424] ${visible || open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"}`}
      >
        {open ? <X size={18} /> : <Compass size={18} className="text-[#E8A33D]" />}
        <span className="whitespace-nowrap">{open ? "Close" : "Ask Scout"}</span>
      </button>

      {open && (
        <div data-testid="scout-panel" className="fixed bottom-20 right-5 z-50 w-[min(92vw,380px)] h-[70vh] max-h-[560px] flex flex-col bg-[#F3F1EC] border-2 border-[#1A1A1A]">
          <div className="bg-[#1A1A1A] text-[#F3F1EC] px-4 py-3 flex items-center gap-2 border-b-2 border-[#E8A33D]">
            <Compass size={18} className="text-[#E8A33D]" />
            <div>
              <div className="font-head uppercase tracking-wide text-sm leading-none">Scout</div>
              <div className="text-[10px] text-[#9CA8B2] mt-0.5">Voluntold's coaching assistant</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} data-testid={`scout-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#2D2D2D] text-[#F3F1EC]" : "bg-[#E7E3D9] text-[#1E1E1E] border-l-2 border-[#E8A33D]"}`}>
                  {m.role === "assistant" ? renderText(m.content) : m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="bg-[#E7E3D9] border-l-2 border-[#E8A33D] px-3 py-2 text-sm text-[#6E6E6E] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Scout is thinking…</div></div>
            )}
            {msgs.length <= 1 && (
              <div className="pt-1 space-y-2" data-testid="scout-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="block w-full text-left text-xs text-[#2D2D2D] border border-[#D3CFC0] hover:border-[#2D2D2D] bg-[#E7E3D9] px-3 py-2 transition-colors">{s}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {handoff ? (
            <form onSubmit={sendHandoff} className="p-3 border-t-2 border-[#D3CFC0] bg-[#E7E3D9] space-y-2" data-testid="scout-handoff-form">
              {hstatus === "ok" ? (
                <div className="text-sm text-[#1E1E1E]" data-testid="handoff-success">
                  Got it — Kyto will get back to you by email.{" "}
                  <button type="button" onClick={() => { setHandoff(false); setHstatus(null); }} className="text-[#2D2D2D] underline">Close</button>
                </div>
              ) : (
                <>
                  <div className="text-xs font-head uppercase tracking-wide text-[#2D2D2D]">Ask Kyto directly</div>
                  <input value={hf.name} onChange={(e) => setHf({ ...hf, name: e.target.value })} placeholder="Your name (optional)"
                    data-testid="handoff-name" className="w-full border-2 border-[#D3CFC0] bg-[#F3F1EC] px-3 py-2 text-sm focus:outline-none focus:border-[#2D2D2D]" />
                  <input type="email" required value={hf.email} onChange={(e) => setHf({ ...hf, email: e.target.value })} placeholder="you@email.com"
                    data-testid="handoff-email" className="w-full border-2 border-[#D3CFC0] bg-[#F3F1EC] px-3 py-2 text-sm focus:outline-none focus:border-[#2D2D2D]" />
                  <textarea required rows="3" value={hf.question} onChange={(e) => setHf({ ...hf, question: e.target.value })} placeholder="Your question for Kyto…"
                    data-testid="handoff-question" className="w-full border-2 border-[#D3CFC0] bg-[#F3F1EC] px-3 py-2 text-sm focus:outline-none focus:border-[#2D2D2D]" />
                  {hstatus === "err" && <div className="text-xs text-[#B44]">Couldn't send. Try again.</div>}
                  <div className="flex gap-2">
                    <button type="submit" disabled={hstatus === "sending"} data-testid="handoff-submit"
                      className="bg-[#2D2D2D] text-[#F3F1EC] font-head uppercase tracking-wide text-xs px-4 py-2 hover:bg-[#242424] transition-colors disabled:opacity-60">
                      {hstatus === "sending" ? "Sending…" : "Send to Kyto"}
                    </button>
                    <button type="button" onClick={() => { setHandoff(false); setHstatus(null); }} className="text-[#6E6E6E] text-sm">Cancel</button>
                  </div>
                </>
              )}
            </form>
          ) : (
            <>
              <button onClick={() => setHandoff(true)} data-testid="scout-ask-kyto"
                className="w-full text-center text-xs font-head uppercase tracking-wide text-[#2D2D2D] py-2 border-t border-[#D3CFC0] hover:bg-[#E7E3D9] transition-colors">
                Want to ask Kyto? →
              </button>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[#D3CFC0] flex gap-2">
                <input
                  maxLength={1000}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  data-testid="scout-input"
                  placeholder="Ask Scout…"
                  className="flex-1 border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 text-sm focus:outline-none focus:border-[#2D2D2D]"
                />
                <button type="submit" disabled={busy} data-testid="scout-send" className="bg-[#E8A33D] text-[#1A1A1A] px-4 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
                  <Send size={16} />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
